import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CreationStatus } from 'redux/middleware/api/creation-service/types';
import {
  embedExportsSelector,
  makeProjectAspectRatioSelector,
  makeProjectConfigSelector,
  makeProjectCreateMethodSelector,
  makeProjectNameSelector,
  makeProjectReprAtSelector,
} from 'redux/modules/entities';
import { hasRestoreEmbedVideoAccessSelector } from 'redux/modules/pricing';
import { regenerationProjectIdSelector } from 'redux/modules/project/selectors';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { LONG_PROJECT_TYPES } from 'utils/constants';
import ProjectPreviewModalContext from './ProjectPreviewModalContext';

export default function useProjectPreviewModalContext() {
  const context = useContext(ProjectPreviewModalContext);

  if (context === undefined) {
    throw new Error(
      'useProjectPreviewModalContext must be used within ProjectPreviewModalProvider',
    );
  }

  return context;
}

export const useProjectWidgetId = () => {
  const { widgetId } = useProjectPreviewModalContext();

  return widgetId;
};

export const useProjectName = () => {
  const { projectId } = useProjectPreviewModalContext();

  return useSelector(
    useMemo(() => makeProjectNameSelector(projectId), [projectId]),
  );
};

export const useReprAt = () => {
  const { projectId } = useProjectPreviewModalContext();

  return useSelector(
    useMemo(() => makeProjectReprAtSelector(projectId), [projectId]),
  );
};

export const useAspectRatio = () => {
  const { projectId } = useProjectPreviewModalContext();

  const aspectRatio = useSelector(
    useMemo(() => makeProjectAspectRatioSelector(projectId), [projectId]),
  );

  return getAspectRatioName(aspectRatio);
};

export const useStatus = () => {
  const { projectLatestRegeneration } = useProjectPreviewModalContext();

  return projectLatestRegeneration?.status;
};

export const useIsStatusNullOrUndefined = () => {
  const status = useStatus();

  return status === null || status === undefined;
};

export const useIsRegeneratingProject = () => {
  const { projectId } = useProjectPreviewModalContext();
  const status = useStatus();
  const regenerationProjectId = useSelector(regenerationProjectIdSelector);
  const widgetId = useProjectWidgetId();

  if (!status && !regenerationProjectId) {
    return false;
  }

  return (
    widgetId &&
    status !== CreationStatus.COMPLETED &&
    status !== CreationStatus.FAILED &&
    regenerationProjectId === projectId
  );
};

export const useIsLoading = () => {
  const {
    embedConfig,
    projectLatestRegeneration,
    isUCSCompatible,
  } = useProjectPreviewModalContext();
  const widgetId = useProjectWidgetId();

  return (
    Boolean(widgetId) &&
    (embedConfig === undefined ||
      projectLatestRegeneration === undefined ||
      isUCSCompatible === undefined)
  );
};

export const useIsShort = () => {
  const { projectId } = useProjectPreviewModalContext();

  const createMethod = useSelector(
    useMemo(() => makeProjectCreateMethodSelector(projectId), [projectId]),
  );

  return !LONG_PROJECT_TYPES.includes(createMethod);
};

export const useIsTranscriptEditable = () => {
  const { projectId } = useProjectPreviewModalContext();

  const projectConfig = useSelector(
    useMemo(() => makeProjectConfigSelector(projectId), [projectId]),
  );

  return projectConfig?.isTranscriptEditable;
};

export const useIsEditable = () => {
  const { projectId } = useProjectPreviewModalContext();

  const projectConfig = useSelector(
    useMemo(() => makeProjectConfigSelector(projectId), [projectId]),
  );

  return projectConfig?.isEditable;
};

export const useEddyProjectId = () => {
  const { embedConfig } = useProjectPreviewModalContext();

  return embedConfig?.captions?.captionSource?.eddyProjectCuid;
};

export const useVideoUrl = () => {
  const embedExports = useSelector(embedExportsSelector);

  const wid = useProjectWidgetId();

  return embedExports?.getIn([wid, 'videoUrl']);
};

export const useFileArchiveInfo = () => {
  const { fileArchiveInfo } = useProjectPreviewModalContext();

  return fileArchiveInfo;
};

export const useEmbedVideoId = () => {
  const { embedVideoId } = useProjectPreviewModalContext();

  return embedVideoId;
};

export const useIsRestrictedArchivedProject = () => {
  const { isFileArchived } = useFileArchiveInfo();
  const hasRestoreEmbedVideoAccess = useSelector(
    hasRestoreEmbedVideoAccessSelector,
  );

  return isFileArchived && !hasRestoreEmbedVideoAccess;
};

export const useIsShareDisabled = () => {
  const widgetId = useProjectWidgetId();
  const isRegeneratingProject = useIsRegeneratingProject();
  const videoUrl = useVideoUrl();

  return isRegeneratingProject || !widgetId || !videoUrl;
};

export const useIsEditDisabled = () => {
  const { isFileArchived } = useFileArchiveInfo();
  const isRegeneratingProject = useIsRegeneratingProject();

  return isFileArchived || isRegeneratingProject;
};
